import { useState, useEffect, useCallback } from 'react';
import type { TenantUserRole } from '@acitra/shared';
import {
  listTenantUsers,
  inviteUser,
  updateUserRole,
  removeUser,
  cancelInvitation,
  type TenantUserWithDetails,
  type TenantInvitation,
} from '../services/api';
import { InviteUserModal } from './InviteUserModal';
import { ChangeRoleModal } from './ChangeRoleModal';
import { RemoveUserModal } from './RemoveUserModal';

interface TeamManagementSectionProps {
  tenantId: string;
  currentUserRole: TenantUserRole;
  currentUserSub: string;
}

const ROLE_LABELS: Record<TenantUserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

const ROLE_COLORS: Record<TenantUserRole, string> = {
  owner: 'bg-purple-100 text-purple-800',
  admin: 'bg-blue-100 text-blue-800',
  member: 'bg-gray-100 text-gray-800',
};

export function TeamManagementSection({
  tenantId,
  currentUserRole,
  currentUserSub,
}: TeamManagementSectionProps) {
  const [users, setUsers] = useState<TenantUserWithDetails[]>([]);
  const [invitations, setInvitations] = useState<TenantInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [changeRoleUser, setChangeRoleUser] = useState<TenantUserWithDetails | null>(null);
  const [removeUserTarget, setRemoveUserTarget] = useState<TenantUserWithDetails | null>(null);

  const canInviteAdmin = currentUserRole === 'owner';

  const fetchTeamData = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await listTenantUsers(tenantId);
      setUsers(data.users);
      setInvitations(data.invitations);
    } catch (err) {
      console.error('Failed to fetch team data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleInvite = async (email: string, role: TenantUserRole) => {
    await inviteUser(tenantId, email, role);
    await fetchTeamData();
  };

  const handleChangeRole = async (cognitoSub: string, newRole: TenantUserRole) => {
    await updateUserRole(tenantId, cognitoSub, newRole);
    await fetchTeamData();
  };

  const handleRemoveUser = async (cognitoSub: string) => {
    await removeUser(tenantId, cognitoSub);
    await fetchTeamData();
  };

  const [cancellingInvitation, setCancellingInvitation] = useState<string | null>(null);

  const handleCancelInvitation = async (invitationId: string) => {
    setCancellingInvitation(invitationId);
    try {
      await cancelInvitation(tenantId, invitationId);
      await fetchTeamData();
    } catch (err) {
      console.error('Failed to cancel invitation:', err);
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    } finally {
      setCancellingInvitation(null);
    }
  };

  const canManageUser = (targetRole: TenantUserRole): boolean => {
    if (currentUserRole === 'owner') return true;
    if (currentUserRole === 'admin' && targetRole === 'member') return true;
    return false;
  };

  const formatDate = (dateString: string) => {
    // Extract YYYY-MM-DD from ISO timestamp
    return dateString.split('T')[0];
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
        </div>
        <div className="px-6 py-8 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Team Members</h2>
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg
            className="-ml-0.5 mr-1.5 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Invite User
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="px-6 py-3 bg-red-50 border-b border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Users list */}
      <div className="divide-y divide-gray-200">
        {users.map((user) => (
          <div key={user.cognitoSub} className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-sm font-medium text-gray-600">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-gray-900">
                    {user.name || user.email}
                    {user.cognitoSub === currentUserSub && (
                      <span className="ml-1 text-xs text-gray-500">(you)</span>
                    )}
                  </p>
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded-full ${ROLE_COLORS[user.role]}`}
                  >
                    {ROLE_LABELS[user.role]}
                  </span>
                </div>
                {user.name && <p className="text-sm text-gray-500">{user.email}</p>}
                <p className="text-xs text-gray-400">Joined {formatDate(user.joinedAt)}</p>
              </div>
            </div>

            {/* Actions */}
            {user.cognitoSub !== currentUserSub && canManageUser(user.role) && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setChangeRoleUser(user)}
                  className="text-sm text-gray-600 hover:text-gray-900"
                >
                  Change role
                </button>
                <button
                  onClick={() => setRemoveUserTarget(user)}
                  className="text-sm text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pending invitations */}
      {invitations.length > 0 && (
        <>
          <div className="px-6 py-3 bg-gray-50 border-t border-b border-gray-200">
            <h3 className="text-sm font-medium text-gray-700">Pending Invitations</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="px-6 py-4 flex items-center justify-between bg-yellow-50/50"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                    <svg
                      className="h-5 w-5 text-yellow-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{invitation.email}</p>
                    <p className="text-xs text-gray-500">
                      Invited as {ROLE_LABELS[invitation.role]} - expires{' '}
                      {formatDate(invitation.expiresAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    disabled={cancellingInvitation === invitation.id}
                    className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
                  >
                    {cancellingInvitation === invitation.id ? 'Cancelling...' : 'Cancel'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Empty state */}
      {users.length === 0 && invitations.length === 0 && (
        <div className="px-6 py-8 text-center">
          <p className="text-sm text-gray-500">
            No team members yet. Invite someone to get started.
          </p>
        </div>
      )}

      {/* Modals */}
      <InviteUserModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvite={handleInvite}
        canInviteAdmin={canInviteAdmin}
      />

      {changeRoleUser && (
        <ChangeRoleModal
          isOpen={!!changeRoleUser}
          onClose={() => setChangeRoleUser(null)}
          onConfirm={(newRole) => handleChangeRole(changeRoleUser.cognitoSub, newRole)}
          user={changeRoleUser}
          canPromoteToAdmin={currentUserRole === 'owner'}
        />
      )}

      {removeUserTarget && (
        <RemoveUserModal
          isOpen={!!removeUserTarget}
          onClose={() => setRemoveUserTarget(null)}
          onConfirm={() => handleRemoveUser(removeUserTarget.cognitoSub)}
          user={removeUserTarget}
        />
      )}
    </div>
  );
}
